
import React from 'react';
import { Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

const ValuesSection = () => {
  const { language } = useLanguage();
  
  const benefits = language === 'en' ? [{
    title: "Benefits for you",
    subtitle: "Benefits for Business Owners",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
    items: [
      {
        title: "Liquidity Options", 
        description: "Flexible liquidity solutions tailored to your financial goals and timeline, allowing you to diversify your personal wealth."
      },
      {
        title: "Customized Deal Structure", 
        description: "We build acquisition terms around your priorities, whether that's upfront cash, earnouts, or ongoing equity participation."
      },
      {
        title: "Fair Valuation", 
        description: "We recognize the true value of what you've built, considering both tangible assets and the intangible elements that make your business special."
      }
    ]
  }, {
    title: "Benefits for your business",
    subtitle: "Benefits for Company Growth",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
    items: [
      {
        title: "Permanent Home", 
        description: "Unlike traditional exits, we provide a forever home for your company, ensuring its legacy continues without the risk of being flipped or dismantled."
      },
      {
        title: "Resources for Growth", 
        description: "Access to capital, technology, and operational expertise to help your business reach its full potential in ways that weren't possible before."
      },
      {
        title: "Market Expansion", 
        description: "Leverage our network and resources to enter new markets and reach customers you couldn't access as a standalone business."
      }
    ]
  }, {
    title: "Benefits for your employees",
    subtitle: "Benefits for Employee Success",
    image: "/lovable-uploads/3979cfb3-f61f-48a9-ad9f-78658796b3b7.png",
    items: [
      {
        title: "Culture Preservation", 
        description: "We respect and maintain the unique culture that makes your team special, preserving the workplace environment they've come to value."
      },
      {
        title: "Professional Development", 
        description: "Enhanced career growth opportunities through training programs, cross-portfolio collaboration, and mentorship from industry experts."
      },
      {
        title: "Stability & Security", 
        description: "Long-term employment security with improved benefits packages and clear paths for advancement within a growing organization."
      }
    ]
  }] : [{
    title: "Beneficios para ti",
    subtitle: "Beneficios para Propietarios de Negocios",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
    items: [
      {
        title: "Opciones de Liquidez", 
        description: "Soluciones de liquidez flexibles adaptadas a tus objetivos financieros y cronograma, permitiéndote diversificar tu patrimonio personal."
      },
      {
        title: "Estructura de Acuerdo Personalizada", 
        description: "Construimos términos de adquisición en torno a tus prioridades, ya sea efectivo por adelantado, pagos condicionados o participación continua de capital."
      },
      {
        title: "Valuación Justa", 
        description: "Reconocemos el verdadero valor de lo que has construido, considerando tanto los activos tangibles como los elementos intangibles que hacen especial a tu negocio."
      }
    ]
  }, {
    title: "Beneficios para tu negocio",
    subtitle: "Beneficios para el Crecimiento de la Empresa",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
    items: [
      {
        title: "Hogar Permanente", 
        description: "A diferencia de las salidas tradicionales, proporcionamos un hogar permanente para tu empresa, asegurando que su legado continúe sin el riesgo de ser revendida o desmantelada."
      },
      {
        title: "Recursos para el Crecimiento", 
        description: "Acceso a capital, tecnología y experiencia operativa para ayudar a tu negocio a alcanzar su máximo potencial de formas que antes no eran posibles."
      },
      {
        title: "Expansión de Mercado", 
        description: "Aprovecha nuestra red y recursos para entrar en nuevos mercados y llegar a clientes a los que no podías acceder como negocio independiente."
      }
    ]
  }, {
    title: "Beneficios para tus empleados",
    subtitle: "Beneficios para el Éxito de los Empleados",
    image: "/lovable-uploads/3979cfb3-f61f-48a9-ad9f-78658796b3b7.png",
    items: [
      {
        title: "Preservación de la Cultura", 
        description: "Respetamos y mantenemos la cultura única que hace especial a tu equipo, preservando el entorno de trabajo que han llegado a valorar."
      },
      {
        title: "Desarrollo Profesional", 
        description: "Oportunidades mejoradas de crecimiento profesional a través de programas de capacitación, colaboración entre portafolios y mentoría de expertos de la industria."
      },
      {
        title: "Estabilidad y Seguridad", 
        description: "Seguridad laboral a largo plazo con paquetes de beneficios mejorados y caminos claros para el avance dentro de una organización en crecimiento."
      }
    ]
  }];

  return (
    <section className="py-36 bg-white dark:bg-noa-rich-black/95 relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/20"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="flex flex-col items-center text-center mb-24">
          <h2 className="font-space-grotesk text-4xl md:text-5xl font-bold text-noa-rich-black dark:text-noa-antiflash-white leading-tight mb-6">
            {language === 'en' ? 
              "Benefits of Partnering with Noa" : 
              "Beneficios de Asociarse con Noa"}<span className="text-blue-600">.</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {language === 'en' ?
              "We create tailored acquisition partnerships that benefit entrepreneurs, their businesses, and teams, ensuring long-term value for all." :
              "Creamos asociaciones de adquisición personalizadas que benefician a emprendedores, empresas y equipos. Nuestro enfoque asegura una transición fluida y un crecimiento sostenible."}
          </p>
        </div>
        
        <div className="flex flex-col space-y-36">
          {benefits.map((category, index) => (
            <div 
              key={index} 
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-16 items-center`}
            >
              <div className="w-full md:w-1/2 h-96 overflow-hidden rounded-xl shadow-lg">
                <img 
                  src={category.image} 
                  alt={category.title} 
                  className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105" 
                />
              </div>
              
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold text-noa-rich-black dark:text-noa-antiflash-white mb-8 font-space-grotesk">
                  {category.title}
                </h3>
                <div className="w-12 h-1 bg-blue-600 mb-8"></div>
                
                <ul className="space-y-6">
                  {category.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <div className="mt-1 bg-blue-500 rounded-full p-1 flex-shrink-0">
                        <Check className="text-white" size={14} strokeWidth={3} />
                      </div>
                      <div>
                        <h4 className="text-noa-rich-black dark:text-noa-antiflash-white font-medium text-lg mb-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          {item.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;
